/**
 * Language Router - Handles URL-based language routing
 * Persists language choice and redirects / to saved language
 */
import React, { useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supportedLanguageCodes, defaultLanguage, loadLanguage } from '../i18n/config';

const LANG_STORAGE_KEY = 'cityredbus_language';

// Hook to get current language from URL
export const useLanguageFromUrl = () => {
  const { lang } = useParams();
  return supportedLanguageCodes.includes(lang) ? lang : defaultLanguage;
};

// Component that syncs URL language with i18n and persists to localStorage
export const LanguageSync = ({ children }) => {
  const { i18n } = useTranslation();
  const { lang } = useParams();

  useEffect(() => {
    // If we have a valid language in URL, sync with i18n AND save to localStorage
    if (lang && supportedLanguageCodes.includes(lang)) {
      if (i18n.language !== lang) {
        // Load translations first if needed, then change language
        loadLanguage(lang).then(() => {
          i18n.changeLanguage(lang);
        });
      }
      // Always persist the current language from URL
      localStorage.setItem(LANG_STORAGE_KEY, lang);
      localStorage.setItem('i18nextLng', lang);
    }
  }, [lang, i18n]);

  return children;
};

// Redirect from root to saved/default language
export const LanguageRedirect = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Priority: 1. Saved preference, 2. Browser language, 3. Default (it)
    const savedLang = localStorage.getItem(LANG_STORAGE_KEY);
    const i18nLang = localStorage.getItem('i18nextLng');
    const browserLang = navigator.language?.split('-')[0];
    
    let targetLang = defaultLanguage;
    
    // Check saved language first
    if (savedLang && supportedLanguageCodes.includes(savedLang)) {
      targetLang = savedLang;
    } else if (i18nLang && supportedLanguageCodes.includes(i18nLang)) {
      targetLang = i18nLang;
    } else if (browserLang && supportedLanguageCodes.includes(browserLang)) {
      targetLang = browserLang;
    }

    // Redirect to language-prefixed URL
    const newPath = `/${targetLang}${location.pathname === '/' ? '' : location.pathname}${location.search}`;
    navigate(newPath, { replace: true });
  }, [navigate, location]);

  return null;
};

// Helper to create localized links
export const useLocalizedPath = () => {
  const { lang } = useParams();
  const currentLang = supportedLanguageCodes.includes(lang) ? lang : defaultLanguage;
  
  return (path) => {
    // If path already has language prefix, return as-is
    const segments = path.split('/').filter(Boolean);
    if (segments.length > 0 && supportedLanguageCodes.includes(segments[0])) {
      return path;
    }
    // Add language prefix
    return `/${currentLang}${path.startsWith('/') ? path : '/' + path}`;
  };
};

// Hook to change language and update URL + persist
export const useChangeLanguage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();
  const { lang } = useParams();

  return async (newLang) => {
    if (!supportedLanguageCodes.includes(newLang)) return;
    
    // Load translations first
    await loadLanguage(newLang);
    
    // Update i18n
    i18n.changeLanguage(newLang);
    
    // Persist to localStorage
    localStorage.setItem(LANG_STORAGE_KEY, newLang);
    localStorage.setItem('i18nextLng', newLang);
    
    // Update URL
    const currentPath = location.pathname;
    let newPath;
    
    if (lang && supportedLanguageCodes.includes(lang)) {
      // Replace current language in path
      newPath = currentPath.replace(`/${lang}`, `/${newLang}`);
    } else {
      // Add language prefix
      newPath = `/${newLang}${currentPath}`;
    }
    
    navigate(newPath + location.search, { replace: true });
  };
};

// Get current saved language (for use outside React components)
export const getSavedLanguage = () => {
  const saved = localStorage.getItem(LANG_STORAGE_KEY);
  return supportedLanguageCodes.includes(saved) ? saved : defaultLanguage;
};

export default LanguageSync;
